import { useEffect, useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import {
  ClipboardList,
  CreditCard,
  Gift,
  GraduationCap,
  HelpCircle,
  Images,
  KeyRound,
  LayoutDashboard,
  Lock,
  LogOut,
  MessagesSquare,
  Package,
  PackageCheck,
  Percent,
  ReceiptText,
  ScrollText,
  ShieldCheck,
  ShoppingBag,
  Star,
  Store,
  TrendingUp,
  Tags,
  UserCheck,
  Users,
  Wallet,
  Activity,
  BadgeDollarSign,
  Sparkles,
  ListOrdered,
  Power,
  MessageCircle,
  Smartphone,
  PencilLine,
  Volume2,
  Megaphone,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSiteStatus } from "@/context/SiteStatusContext";
import { api } from "@/lib/api";
import { applyMenuLayout } from "@/lib/menuLayout";
import PaymentAlert from "@/components/admin/PaymentAlert";
import NotificationBell from "@/components/ai/NotificationBell";
import { OrderAlarmProvider, OrderAlarmSettings } from "@/context/OrderAlarmContext";

const MENU = [
  { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, end: true, testId: "admin-nav-dashboard" },
  { to: "/admin/ia-analytics", label: "IA & Analytics", icon: Sparkles, testId: "admin-nav-ai" },
  { to: "/admin/produits", label: "Produits", icon: Package, testId: "admin-nav-products" },
  { to: "/admin/categories", label: "Catégories", icon: Tags, testId: "admin-nav-categories" },
  { to: "/admin/licences", label: "Licences & clés", icon: KeyRound, testId: "admin-nav-licenses" },
  { to: "/admin/formations", label: "Formations", icon: GraduationCap, testId: "admin-nav-formations" },
  { to: "/admin/formations/etudiants", label: "Étudiants & accès", icon: UserCheck, testId: "admin-nav-formation-students" },
  { to: "/admin/offres", label: "Offres groupées", icon: Gift, testId: "admin-nav-bundles" },
  { to: "/admin/commandes", label: "Commandes", icon: ShoppingBag, testId: "admin-nav-orders" },
  { to: "/admin/livraisons", label: "Livraisons numériques", icon: PackageCheck, testId: "admin-nav-deliveries" },
  { to: "/admin/paiements-verifier", label: "Paiements à vérifier", icon: ReceiptText, testId: "admin-nav-payments-verify" },
  { to: "/admin/moyens-paiement", label: "Moyens de paiement", icon: CreditCard, testId: "admin-nav-payments" },
  { to: "/admin/clients", label: "Clients", icon: Users, testId: "admin-nav-customers" },
  { to: "/admin/revendeurs", label: "Revendeurs", icon: Store, testId: "admin-nav-vendors", end: true },
  { to: "/admin/revendeurs/ventes", label: "Ventes revendeurs", icon: TrendingUp, testId: "admin-nav-vendor-sales" },
  { to: "/admin/revendeurs/demandes", label: "Demandes revendeurs", icon: Wallet, testId: "admin-nav-vendor-requests" },
  { to: "/admin/revendeurs/abonnements", label: "Abonnements revendeurs", icon: BadgeDollarSign, testId: "admin-nav-vendor-plans" },
  { to: "/admin/revendeurs/activites", label: "Activité revendeurs", icon: Activity, testId: "admin-nav-vendor-activities" },
  { to: "/admin/bannieres", label: "Bannière / Slider", icon: Images, testId: "admin-nav-slides" },
  { to: "/admin/promotions", label: "Codes promo", icon: Percent, testId: "admin-nav-promos" },
  { to: "/admin/avis", label: "Avis clients", icon: Star, testId: "admin-nav-reviews" },
  { to: "/admin/tickets", label: "Tickets support", icon: MessagesSquare, testId: "admin-nav-tickets" },
  { to: "/admin/journal", label: "Journal d'activité", icon: ScrollText, testId: "admin-nav-logs" },
  { to: "/admin/contenu", label: "Contenu & FAQ", icon: HelpCircle, testId: "admin-nav-content" },
  { to: "/admin/menus", label: "Positions des menus", icon: ListOrdered, testId: "admin-nav-menus" },
  { to: "/admin/maintenance", label: "Maintenance & annonces", icon: Power, testId: "admin-nav-maintenance" },
  { to: "/admin/whatsapp", label: "Historique WhatsApp", icon: MessageCircle, testId: "admin-nav-whatsapp" },
  { to: "/admin/message-whatsapp", label: "Message WhatsApp", icon: PencilLine, testId: "admin-nav-wa-message" },
  { to: "/admin/pwa", label: "Icône PWA (app)", icon: Smartphone, testId: "admin-nav-pwa" },
  { to: "/admin/sonneries", label: "Sonnerie d'alarme", icon: Volume2, testId: "admin-nav-alarm-sounds" },
  { to: "/admin/promo-popup", label: "Fenêtre promo", icon: Images, testId: "admin-nav-promo-popup" },
  { to: "/admin/publications", label: "Nos publications", icon: Megaphone, testId: "admin-nav-publications" },
];

const PRIVATE_ITEM = { to: "/admin/notes-privees", label: "Mes notes privées", icon: Lock, testId: "admin-nav-private-notes" };

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { resellers } = useSiteStatus();
  const resellersOn = resellers?.enabled !== false;
  const navigate = useNavigate();
  const [pending, setPending] = useState(0);
  const [payments, setPayments] = useState(0);
  const [vendorReqs, setVendorReqs] = useState(0);
  const [menuLayout, setMenuLayout] = useState(null);

  useEffect(() => {
    api.get("/admin/menu-layout").then(({ data }) => setMenuLayout(data.admin)).catch(() => {});
  }, []);

  const displayMenu = applyMenuLayout(MENU, menuLayout)
    .filter((item) => resellersOn || !item.to.startsWith("/admin/revendeurs"));

  useEffect(() => {
    let active = true;
    const load = () => {
      api.get("/admin/stats").then(({ data }) => {
        if (!active) return;
        setPending(data.pending_activations || 0);
        setPayments(data.payments_to_verify || 0);
      }).catch(() => {});
      api.get("/admin/vendor-requests-count").then(({ data }) => {
        if (!active) return;
        setVendorReqs(data.total || 0);
      }).catch(() => {});
    };
    load();
    const timer = setInterval(load, 60000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, []);

  return (
    <OrderAlarmProvider role="admin" ordersPath="/admin/commandes">
    <div className="min-h-screen bg-surface">
      <header className="sticky top-0 z-40 border-b border-line bg-white">
        <div className="mx-auto flex h-16 max-w-[1600px] items-center gap-4 px-4 sm:px-6">
          <Link to="/admin" className="flex items-center gap-2.5" data-testid="admin-logo">
            <img src="/logo-mark.png" alt="ISMO-TECH" className="h-9 w-auto shrink-0 object-contain" />
            <span className="font-display text-lg font-extrabold text-ink">
              ISMO<span className="text-brand">-TECH</span>
              <span className="ml-2 rounded bg-brand-light px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand">
                Admin
              </span>
            </span>
          </Link>

          <div className="ml-auto flex items-center gap-2">
            <OrderAlarmSettings />
            <NotificationBell />
            <Link
              to="/"
              data-testid="admin-view-site"
              className="hidden rounded-full border border-line px-4 py-2 text-xs font-bold text-ink transition-colors duration-200 hover:border-brand hover:text-brand sm:block"
            >
              Voir le site
            </Link>
            <span className="hidden text-xs text-slate-500 md:block">{user?.email}</span>
            <button
              data-testid="admin-logout"
              onClick={async () => {
                await logout();
                navigate("/");
              }}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-rose-600 transition-colors duration-200 hover:bg-rose-50"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1600px] gap-6 px-4 py-6 sm:px-6">
        <aside className="hidden w-60 shrink-0 lg:block">
          <nav className="sticky top-[88px] space-y-1">
            {displayMenu.map(({ to, label, icon: Icon, end, testId }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={testId}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-[13.5px] font-semibold transition-colors duration-200 ${
                    isActive ? "bg-ink text-white" : "text-slate-600 hover:bg-white hover:text-ink"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {label}
                {to === "/admin/commandes" && pending > 0 && (
                  <span
                    data-testid="admin-pending-badge"
                    className="ml-auto rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-black text-white"
                  >
                    {pending}
                  </span>
                )}
                {to === "/admin/paiements-verifier" && payments > 0 && (
                  <span
                    data-testid="admin-payments-badge"
                    className="ml-auto rounded-full bg-orange-500 px-2 py-0.5 text-[10px] font-black text-white"
                    title="Paiements à vérifier"
                  >
                    {payments}
                  </span>
                )}
                {to === "/admin/revendeurs/demandes" && vendorReqs > 0 && (
                  <span
                    data-testid="admin-vendor-requests-badge"
                    className="ml-auto rounded-full bg-rose-500 px-2 py-0.5 text-[10px] font-black text-white"
                    title="Demandes des revendeurs en attente"
                  >
                    {vendorReqs}
                  </span>
                )}
              </NavLink>
            ))}
            <div className="pt-4">
              <p className="px-3.5 pb-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                Mon espace privé
              </p>
              <NavLink
                to={PRIVATE_ITEM.to}
                data-testid={PRIVATE_ITEM.testId}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-[13.5px] font-semibold transition-colors duration-200 ${
                    isActive ? "bg-ink text-white" : "text-slate-600 hover:bg-white hover:text-ink"
                  }`
                }
              >
                <PRIVATE_ITEM.icon className="h-4 w-4" />
                {PRIVATE_ITEM.label}
              </NavLink>
            </div>
          </nav>
        </aside>

        <div className="min-w-0 flex-1">
          <nav className="mb-4 flex gap-2 overflow-x-auto no-scrollbar lg:hidden">
            {[...displayMenu, PRIVATE_ITEM].map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `flex shrink-0 items-center gap-2 rounded-full px-3.5 py-2 text-xs font-bold transition-colors duration-200 ${
                    isActive ? "bg-ink text-white" : "border border-line bg-white text-slate-600"
                  }`
                }
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </NavLink>
            ))}
          </nav>
          <PaymentAlert />
          <Outlet />
        </div>
      </div>
    </div>
    </OrderAlarmProvider>
  );
}

export { MENU as ADMIN_MENU };
